/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, {useState, FC, useEffect} from 'react'
import {Row, Col, Pagination, Form, Spinner, Dropdown, Button} from 'react-bootstrap'
import {ListComodityResultRowType} from '../core/_models'
import {COMTRADE_DATA_YEAR} from '../../../../libs/constant'
import {formatNumber, getNameByISO3} from '../../../../libs/misc'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListComodityTableProps = {
  rows?: ListComodityResultRowType[] | null
  counts: number
  isLoading: boolean
  sourceType?: string
  onImportClick?: () => void
}

const ListComodity: FC<ListComodityTableProps> = ({
  rows,
  counts,
  isLoading,
  sourceType,
  onImportClick,
}) => {
  const [internalLoading, setInternalLoading] = useState(true)
  const [listComodity, setListComodity] = useState<ListComodityResultRowType[]>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const comoditySortFn = (rowA, rowB, columnId) => {
    let valueA = rowA.getValue(columnId)
    let valueB = rowB.getValue(columnId)

    if (valueA == null && valueB == null) return 0
    if (valueA == null) return 1
    if (valueB == null) return -1

    const numA = parseFloat(valueA)
    const numB = parseFloat(valueB)

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB // ascending
    }

    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0
  }

  const columnHelper = createColumnHelper<ListComodityResultRowType>()

  const columns = [
    {
      accessorKey: 'hs',
      header: 'HS Code',
      cell: (info) => {
        return <span className='badge text-white bg-dark'>{info.getValue()}</span>
      },
      sortingFn: comoditySortFn,
      meta: {
        headerClass: 'w-100px text-center',
        cellClass: 'text-center',
      },
    },
    {
      accessorKey: 'hs_description',
      header: 'Commodity Description',
      cell: (info) => info.getValue(),
      meta: {
        headerClass: 'w-200px',
        cellClass: '',
      },
    },
    {
      accessorKey: 'par',
      header: 'Partner',
      cell: (info) => {
        return getNameByISO3(info.getValue())
      },
      sortingFn: comoditySortFn,
      meta: {
        headerClass: 'w-200px',
        cellClass: '',
      },
    },
    {
      accessorKey: 'val',
      header: 'Value',
      cell: (info) => formatNumber(info.getValue()),
      sortingFn: comoditySortFn,
      meta: {
        headerClass: 'w-100px text-end',
        cellClass: 'text-end',
      },
    },
    {
      accessorKey: 'qty',
      header: 'Qty',
      cell: (info) => formatNumber(info.getValue()),
      sortingFn: comoditySortFn,
      meta: {
        headerClass: 'w-100px text-end',
        cellClass: 'text-end',
      },
    },
  ]

  // Cleanup function to reset the state when the component is unmounted
  useEffect(() => {
    setListComodity([])
    setInternalLoading(true)
    return () => {
      setListComodity([])
      setInternalLoading(true)
    }
  }, [])

  useEffect(() => {
    setListComodity(rows || [])
    setInternalLoading(isLoading)
  }, [rows, isLoading])

  const dataTable = useReactTable({
    data: listComodity || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil(counts / pagination.pageSize), // or use a fixed total if backend provides it
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handlePageChange = (page) => {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page - 1)
    }
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  const totalCount = dataTable.getRowCount()
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1
  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length
  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div
              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                    ? 'Sort descending'
                    : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              <span className='ms-2'>
                {{
                  asc: <i className='fa-solid fa-sort-up'></i>,
                  desc: <i className='fa-solid fa-sort-down'></i>,
                  false: <i className='fa-solid fa-sort'></i>,
                }[header.column.getIsSorted() as string] ?? null}
              </span>
            </div>
          </th>
        )
      })}
    </tr>
  ))
  const displayBody = dataTable.getRowModel().rows.map((row) => (
    <tr key={row.id} className='align-middle'>
      {row.getVisibleCells().map((cell) => {
        return (
          <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  ))
  return (
    <>
      {!internalLoading ? (
        <>
          <Row className='mb-5'>
            <Col lg={7} className='d-flex justify-content-start align-items-center'>
              <i className='fa-solid fa-circle text-success fs-9 me-2'></i>Showing{' '}
              {listComodity?.length < 1 ? 0 : firstIndexPerPages} to {lastIndexPerPages} of{' '}
              {totalCount} results
            </Col>
            <Col lg={5} className='d-flex justify-content-end align-items-center gap-5'>
              <Form.Group className='form-group w-50' controlId=''>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({
                        pageIndex: 1,
                        pageSize: 10,
                      })
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
              {sourceType === 'manual' && (
                <Button variant='outline-primary' className='btn-sm h-100' onClick={onImportClick}>
                  Import Data
                </Button>
              )}
            </Col>
          </Row>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_country_tradeflow'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>
              {(listComodity?.length ?? 0) > 0 ? (
                displayBody
              ) : (
                <tr>
                  <td colSpan={99} className='text-center'>
                    No Data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              trade flows
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => dataTable.previousPage()}
                  disabled={!dataTable.getCanPreviousPage()}
                />
                {/* Start Dynamic Page */}
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => dataTable.nextPage()}
                  disabled={!dataTable.getCanNextPage()}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </>
  )
}

export {ListComodity}
